import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import {
    dateOfBirthSchema,
    emptyToNull,
    getErrorMessage,
    MAX_INPUT_FIELD_LENGTH,
    optionalEmailSchema,
    usAddressSchema,
    usPhoneSchema,
} from './client-profile.schema';
import { CLIENT_TIME_ZONES } from './client-profile.time-zones';
import { clientIdSchema, personIdSchema } from './ids';

export const CLIENT_ADD_CONTACT_TYPE = 'client/add-contact';
export const CLIENT_ADD_PRACTICE_MEMBER_TYPE = 'client/add-practice-member';
export const CLIENT_ATTACH_CONTACT_TYPE = 'client/attach-contact';
export const CLIENT_CREATE_TYPE = 'client/create';
export const CLIENT_SET_CONSENT_TYPE = 'client/set-consent';
export const CLIENT_UPDATE_CONTACT_PROFILE_TYPE = 'client/update-contact-profile';
export const CLIENT_UPDATE_PROFILE_TYPE = 'client/update-profile';

export { MAX_INPUT_FIELD_LENGTH } from './client-profile.schema';

export const MAX_CONTACT_NOTES_LENGTH = 1000;

export const CONTACT_RELATIONSHIPS = [
    'FAMILY_MEMBER',
    'FRIEND',
    'LEGAL_GUARDIAN',
    'PARENT',
    'SPOUSE_PARTNER',
    'PHYSICIAN',
    'OTHER',
] as const;

export const clientCreate = defineCommand(
    CLIENT_CREATE_TYPE,
    z.object({
        firstName: z
            .string()
            .trim()
            .min(1, 'Enter first name')
            .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('First name')),
        lastName: z
            .string()
            .trim()
            .min(1, 'Enter last name')
            .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Last name')),
        preferredFirstName: z
            .string()
            .trim()
            .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Preferred first name'))
            .optional()
            // `Person.create` stores `''` as-is, so "none" is made absent here to match the edit contract's null
            .transform((name) => name || undefined),
        emailPrimary: optionalEmailSchema.optional(),
        phonePrimary: emptyToNull(usPhoneSchema).optional(),
        pronouns: z
            .string()
            .trim()
            .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Pronouns'))
            .nullable()
            .optional(),
        recordingConsent: z.boolean().optional().default(false).meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
    // `name` is the composed display name saved to `clients.name`, so a caller never recomposes it
    z.object({ id: z.uuid(), name: z.string() }),
);

export const clientAddPracticeMember = defineCommand(
    CLIENT_ADD_PRACTICE_MEMBER_TYPE,
    z.object({
        id: clientIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);

export const clientSetConsent = defineCommand(
    CLIENT_SET_CONSENT_TYPE,
    z.object({
        id: clientIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);

// Exported unrefined so the app can `.omit()` the server-supplied fields — zod rejects `.omit()` on
// a schema that already carries refinements, so the refinement is re-applied there instead.
export const clientUpdateProfileBaseSchema = z.object({
    id: clientIdSchema.meta(metadata.nonSensitive),
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),

    firstName: z
        .string()
        .trim()
        .min(1, 'Enter first name')
        .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('First name')),
    middleName: z.string().trim().max(1, 'Use a single letter').nullable(),
    lastName: z
        .string()
        .trim()
        .min(1, 'Enter last name')
        .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Last name')),
    // Normalised here, not with `emptyToNull`: that helper deliberately does not trim, and making it
    // trim would let `dateOfBirth` and `phonePrimary` accept whitespace-only input.
    preferredFirstName: z
        .string()
        .trim()
        .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Preferred first name'))
        .nullable()
        .transform((name) => (name === '' ? null : name)),
    dateOfBirth: emptyToNull(dateOfBirthSchema),

    emailPrimary: optionalEmailSchema,
    phonePrimary: emptyToNull(usPhoneSchema),
    timeZone: z.enum(CLIENT_TIME_ZONES).nullable(),

    address: usAddressSchema,

    okToEmail: z.boolean().nullable(),
    okToCallText: z.boolean().nullable(),
    emailReminders: z.boolean(),
    textReminders: z.boolean(),

    sex: z.enum(['male', 'female', 'other']).nullable(),
    // the form requires text while "Other" is the selected choice; storage keeps one free-text field
    pronouns: z.string().trim().max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Pronouns')).nullable(),
});

export const clientUpdateProfile = defineCommand(
    CLIENT_UPDATE_PROFILE_TYPE,
    clientUpdateProfileBaseSchema,
);

// The shared fields are picked from the client schema, not retyped, so the two forms cannot drift on
// rules or messages. Exported unrefined for the same reason as the profile.
export const clientUpdateContactProfileBaseSchema = z.object({
    id: personIdSchema.meta(metadata.nonSensitive),
    practiceId: clientUpdateProfileBaseSchema.shape.practiceId,
    practiceMemberId: clientUpdateProfileBaseSchema.shape.practiceMemberId,

    ...clientUpdateProfileBaseSchema.pick({
        firstName: true,
        lastName: true,
        preferredFirstName: true,
        emailPrimary: true,
        phonePrimary: true,
        timeZone: true,
        address: true,
        pronouns: true,
    }).shape,

    // Re-marked here only: the client profile redacts them with the rest of its communication
    // choices, but on a contact they are the two flags the log keeps to locate what changed.
    okToEmail: clientUpdateProfileBaseSchema.shape.okToEmail.meta(metadata.nonSensitive),
    okToCallText: clientUpdateProfileBaseSchema.shape.okToCallText.meta(metadata.nonSensitive),
});

export const clientUpdateContactProfile = defineCommand(
    CLIENT_UPDATE_CONTACT_PROFILE_TYPE,
    clientUpdateContactProfileBaseSchema,
);

// Exported unrefined for the same reason as the profile: the app `.omit()`s the server-supplied ids.
export const clientAddContactBaseSchema = z.object({
    id: clientIdSchema.meta(metadata.nonSensitive),
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),

    firstName: z
        .string()
        .trim()
        .min(1, 'Enter first name')
        .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('First name')),
    lastName: z
        .string()
        .trim()
        .min(1, 'Enter last name')
        .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Last name')),
    emailPrimary: optionalEmailSchema,
    phonePrimary: emptyToNull(usPhoneSchema),
    address: usAddressSchema,

    relationship: z.enum(CONTACT_RELATIONSHIPS, 'Select relationship').meta(metadata.nonSensitive),
    isEmergencyContact: z.boolean().default(false).meta(metadata.nonSensitive),
    isBillingContact: z.boolean().default(false).meta(metadata.nonSensitive),

    notes: z
        .string()
        .trim()
        .max(
            MAX_CONTACT_NOTES_LENGTH,
            `Notes must be at most ${MAX_CONTACT_NOTES_LENGTH} characters`,
        )
        .nullable()
        .transform((notes) => (notes === '' ? null : notes)),
});

export const clientAddContact = defineCommand(CLIENT_ADD_CONTACT_TYPE, clientAddContactBaseSchema);

// The names are locked on the Existing contact tab, so this command takes none: sending them would
// be the one way it could rename somebody. Exported unrefined for the same `.omit()` reason.
export const clientAttachContactBaseSchema = z.object({
    id: clientIdSchema.meta(metadata.nonSensitive),
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    personId: personIdSchema.meta(metadata.nonSensitive),

    emailPrimary: clientAddContactBaseSchema.shape.emailPrimary,
    phonePrimary: clientAddContactBaseSchema.shape.phonePrimary,
    address: clientAddContactBaseSchema.shape.address,

    relationship: clientAddContactBaseSchema.shape.relationship,
    isEmergencyContact: clientAddContactBaseSchema.shape.isEmergencyContact,
    isBillingContact: clientAddContactBaseSchema.shape.isBillingContact,

    notes: clientAddContactBaseSchema.shape.notes,
});

export const clientAttachContact = defineCommand(
    CLIENT_ATTACH_CONTACT_TYPE,
    clientAttachContactBaseSchema,
);
